import { useMutation } from "@apollo/client";
import Add from "@mui/icons-material/Add";
import {
  Box,
  Button,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { EpisodicEventItemFragment, WasteEpisodicEventStatus } from "generated-graphql/graphql";
import { useState } from "react";

import { formatQuantity } from "../Containers/containerDisplay";
import { WasteContainerDialog } from "../Containers/WasteContainerDialog";
import { WasteContainerDrawer } from "../Containers/WasteContainerDrawer";
import { WasteContainerStatusChip } from "../Containers/WasteContainerStatusChip";
import { SET_CONTAINER_EPISODIC_EVENT_MUTATION } from "./api";
import { AttachContainerDialog, describeAttachError } from "./AttachContainerDialog";
import { isEventWindowLapsed } from "./episodicDisplay";

type EventContainer = EpisodicEventItemFragment["containers"][number];

const REFETCH_QUERIES = ["FacilityEpisodicEvents", "FacilityAccumulationSummary"];

/** Default rows per page for each event's container list. */
const CONTAINERS_PAGE_SIZE = 5;

/** One tied-container row: container link, waste stream, area, amount, and
 * the container status chip, plus a Detach action (ACTIVE events only). The
 * label opens the container drawer over this page — navigating to the
 * container grid would drop the reader out of the event they're working. */
function ContainerRow({
  container,
  canDetach,
  onView,
  onDetach,
  detaching,
}: {
  container: EventContainer;
  canDetach: boolean;
  onView: (containerId: string) => void;
  onDetach: (containerId: string) => void;
  detaching: boolean;
}) {
  return (
    <TableRow>
      <TableCell>
        <Link component="button" variant="body2" onClick={() => onView(container.id)}>
          {container.containerLabel}
        </Link>
      </TableCell>
      <TableCell>{container.wasteStream?.name ?? "—"}</TableCell>
      <TableCell>
        {container.accumulationArea
          ? `${container.accumulationArea.name} (${container.accumulationArea.type})`
          : "—"}
      </TableCell>
      <TableCell>{formatQuantity(container.amount, container.amountUnit)}</TableCell>
      <TableCell>
        <WasteContainerStatusChip status={container.status} />
      </TableCell>
      <TableCell align="right">
        {canDetach && (
          <Button size="small" disabled={detaching} onClick={() => onDetach(container.id)}>
            Detach
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}

/** The tied-container table with its client-side pager. */
function EventContainersTable({
  containers,
  pagedContainers,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  canDetach,
  onView,
  onDetach,
  detaching,
}: {
  containers: EventContainer[];
  pagedContainers: EventContainer[];
  page: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  canDetach: boolean;
  onView: (containerId: string) => void;
  onDetach: (container: EventContainer) => void;
  detaching: boolean;
}) {
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Container</TableCell>
            <TableCell>Waste stream</TableCell>
            <TableCell>Area</TableCell>
            <TableCell>Amount</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {pagedContainers.map((container) => (
            <ContainerRow
              key={container.id}
              container={container}
              canDetach={canDetach}
              onView={onView}
              onDetach={() => onDetach(container)}
              detaching={detaching}
            />
          ))}
        </TableBody>
      </Table>
      {containers.length > CONTAINERS_PAGE_SIZE && (
        <TablePagination
          component="div"
          count={containers.length}
          page={page}
          onPageChange={(_event, nextPage) => onPageChange(nextPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => onRowsPerPageChange(Number(e.target.value))}
          rowsPerPageOptions={[5, 10, 25]}
        />
      )}
    </TableContainer>
  );
}

/**
 * Containers currently tied to one episodic event, with a header caption, an
 * attach action (un-lapsed ACTIVE events only), and a per-row detach action
 * (ACTIVE events only — a finalized or expired event's window is settled).
 * Mounted inside each `EpisodicEventCard`.
 */
/**
 * The attach-mutation wiring shared by create-and-attach and detach.
 * Create-then-attach: the standard container dialog creates the container,
 * then it joins the event via the same attach mutation the picker uses. A
 * failed attach leaves a perfectly good container behind — the message says
 * so instead of pretending the whole thing failed.
 */
function useEventContainerMutations(eventId: string, onDetachSettled: () => void) {
  const alerts = useAlerts();
  const [setContainerEpisodicEvent, { loading: detaching }] = useMutation(
    SET_CONTAINER_EPISODIC_EVENT_MUTATION,
    { refetchQueries: REFETCH_QUERIES }
  );

  const handleCreated = async (containerId: string) => {
    await setContainerEpisodicEvent({
      variables: { containerId, episodicEventId: eventId },
      onCompleted: () => {
        alerts.success("Container created and attached to event");
      },
      onError: (error) => {
        alerts.error(
          `Container created, but it couldn't join the event: ${describeAttachError(
            error,
            "an error occurred while attaching it"
          )}`
        );
      },
    });
  };

  const handleDetach = async (containerId: string) => {
    await setContainerEpisodicEvent({
      variables: { containerId, episodicEventId: null },
      onCompleted: () => {
        alerts.success("Container detached from event");
        onDetachSettled();
      },
      onError: (error) => {
        alerts.error(error.message || "An error occurred while detaching the container");
        onDetachSettled();
      },
    });
  };

  return { handleCreated, handleDetach, detaching };
}

export function EventContainersSection({ event }: { event: EpisodicEventItemFragment }) {
  const [attachOpen, setAttachOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [viewContainerId, setViewContainerId] = useState<string | null>(null);
  const [detachCandidate, setDetachCandidate] = useState<EventContainer | null>(null);
  const { handleCreated, handleDetach, detaching } = useEventContainerMutations(event.id, () =>
    setDetachCandidate(null)
  );

  const isActive = event.status === WasteEpisodicEventStatus.Active;
  const canAttach = isActive && !isEventWindowLapsed(event.accumulationDeadline);

  // Client-side pagination: the event fragment already carries every tied
  // container (the SDL has no per-event container paging), so slicing here
  // costs nothing extra. Clamp instead of trusting raw state so detaching the
  // last row of the last page can't strand the view on an empty page.
  const [rawPage, setRawPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(CONTAINERS_PAGE_SIZE);
  const lastPage = Math.max(0, Math.ceil(event.containers.length / rowsPerPage) - 1);
  const page = Math.min(rawPage, lastPage);
  const pagedContainers = event.containers.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
        sx={{ mb: 1 }}
      >
        <Typography variant="caption" color="text.secondary">
          {isActive
            ? `${event.containers.length} tied · clocking to the 60-day window`
            : `${event.containers.length} tied`}
        </Typography>
        {canAttach && (
          <Stack direction="row" spacing={1}>
            <Button size="small" onClick={() => setAttachOpen(true)}>
              Attach existing containers
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<Add />}
              onClick={() => setCreateOpen(true)}
            >
              New container
            </Button>
          </Stack>
        )}
      </Stack>

      {event.containers.length > 0 && (
        <EventContainersTable
          containers={event.containers}
          pagedContainers={pagedContainers}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setRawPage}
          onRowsPerPageChange={(next) => {
            setRowsPerPage(next);
            setRawPage(0);
          }}
          canDetach={isActive}
          onView={setViewContainerId}
          onDetach={setDetachCandidate}
          detaching={detaching}
        />
      )}

      {viewContainerId && (
        <WasteContainerDrawer
          containerId={viewContainerId}
          open
          onClose={() => setViewContainerId(null)}
        />
      )}

      {attachOpen && (
        <AttachContainerDialog
          eventId={event.id}
          open={attachOpen}
          onClose={() => setAttachOpen(false)}
        />
      )}

      {createOpen && (
        <WasteContainerDialog
          facilityId={event.facilityId}
          onClose={() => setCreateOpen(false)}
          onCreated={handleCreated}
          episodicEvent={{
            reason: event.reason,
            startDate: event.startDate,
            accumulationDeadline: event.accumulationDeadline,
          }}
        />
      )}

      <ConfirmDialog
        open={detachCandidate != null}
        onClose={() => setDetachCandidate(null)}
        onConfirm={() => {
          if (detachCandidate) {
            void handleDetach(detachCandidate.id);
          }
        }}
        loading={detaching}
        header={`Detach ${detachCandidate?.containerLabel ?? "container"}?`}
        info="Its waste stops being episodic and counts as routine generation again. This can affect the facility's on-site cap and generator status."
        confirmText="Detach"
      />
    </Box>
  );
}
